import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Crossicon } from "../../../../../assests/homeScreen/crossicon.svg";
import { pendingApproval } from "../../../../../redux/pendingApprovalSlice";
import { RefreshToken } from "../../../../../redux/authSlice";
import { Box, Modal, Typography, Button, Card, Chip } from '@mui/material';
import MSTextField from '../../../../../customTheme/textField/MSTextField';
import { isMobile } from 'react-device-detect';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to construct full image URL for display
const getImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/150x200?text=No+Image';
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    // For relative paths starting with /uploads/, keep them relative (served from same domain in production)
    if (url.startsWith('/uploads/')) {
        return url;
    }
    return url;
};

const PendingApproval = (props) => {
    const { refreshToken, accessToken, userRole } = useSelector((state) => state.login);
    const { loading } = useSelector((state) => state.pendingImageApproval)
    const [selectedItem, setSelectedItem] = React.useState(null);
    const [pendingImages, setPendingImages] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [rejectReason, setRejectReason] = React.useState('');
    const [actionLoading, setActionLoading] = React.useState(false);
    const dispatch = useDispatch();

    const handleClose = () => {
        setOpen(false);
        setRejectReason('');
        setSelectedItem(null);
    }

    const fetchPendingImages = React.useCallback(() => {
        let header = {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`
        }
        if (userRole === "ADMIN" || userRole === "SCHOOL_ADMIN" || userRole === "SUPER_ADMIN") {
            dispatch(pendingApproval({
                headers: header,
                method: 'GET',
                body: {
                    limit: 50,
                }
            })).then((res) => {
                // Handle the response - check both images and pendingImages arrays
                if (res.payload) {
                    const images = res.payload.images || res.payload.pendingImages || res.payload;
                    if (Array.isArray(images)) {
                        setPendingImages(images);
                    } else {
                        setPendingImages([]);
                    }
                } else {
                    setPendingImages([]);
                }
            }).catch((err) => {
                console.error('Error fetching pending approvals:', err);
                setPendingImages([]);
            });
        }
    }, [accessToken, userRole, dispatch]);

    React.useEffect(() => {
        fetchPendingImages();
    }, [fetchPendingImages]);

    const handleImageOpen = (item) => {
        setOpen(true);
        setSelectedItem(item);
    }

    const handleFieldsChange = (e, fieldName) => {
        e.preventDefault()
        setRejectReason(e.target.value);
    }

    const handleApprove = async (e) => {
        e.preventDefault();
        if (!selectedItem?.id) return;
        
        setActionLoading(true);
        try {
            await axios.post(
                `${BACKEND_URL}/api/rest/images/admin/approveImage`,
                { imageId: selectedItem.id },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            handleClose();
            fetchPendingImages(); // Refresh the list
        } catch (err) {
            console.error('Error approving image:', err);
            alert('Failed to approve image: ' + (err.response?.data?.detail || err.message));
        } finally {
            setActionLoading(false);
        }
    }

    const handleReject = async (e) => {
        e.preventDefault();
        if (!selectedItem?.id || !rejectReason.trim()) {
            alert('Please enter a reason for rejection');
            return;
        }
        
        setActionLoading(true);
        try {
            await axios.post(
                `${BACKEND_URL}/api/rest/images/admin/rejectImage`,
                { imageId: selectedItem.id, reason: rejectReason },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            handleClose();
            fetchPendingImages(); // Refresh the list
        } catch (err) {
            console.error('Error rejecting image:', err);
            alert('Failed to reject image: ' + (err.response?.data?.detail || err.message));
        } finally {
            setActionLoading(false);
        }
    }

    const renderPendingImages = () => {
        const images = Array.isArray(pendingImages) ? pendingImages : [];
        if (images.length === 0 && !loading)
            return <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>No Pending Images Found</p>
        else if (images.length === 0 && loading)
            return <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>Loading...</p>
        else {
            return (
                <div className="pendingImageContainer" style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '20px',
                    padding: '20px',
                    justifyContent: isMobile ? 'space-evenly' : 'flex-start' 
                }}>
                    {images.map((img, i) => {
                        const imageUrl = getImageUrl(img.url);
                        return (
                            <div 
                                key={img.id || i} 
                                className="pendingImageSubContainer" 
                                style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    width: isMobile ? '45%' : '180px',
                                    cursor: 'pointer',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    backgroundColor: '#fff',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                onClick={() => handleImageOpen(img)}
                            >
                                <div style={{ 
                                    width: '100%', 
                                    height: '150px', 
                                    backgroundColor: '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}>
                                    <img 
                                        src={imageUrl} 
                                        alt={img.title || 'Pending Image'} 
                                        style={{ 
                                            maxWidth: '100%', 
                                            maxHeight: '100%', 
                                            objectFit: 'cover' 
                                        }}
                                        onError={(e) => { 
                                            e.target.src = 'https://via.placeholder.com/150x150?text=Image+Error'; 
                                        }}
                                    />
                                </div>
                                <div style={{ padding: '10px' }}>
                                    <p style={{ 
                                        margin: 0, 
                                        fontWeight: 'bold', 
                                        fontSize: '14px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {img.title || 'Untitled'}
                                    </p>
                                    <p style={{ 
                                        margin: '4px 0 0 0', 
                                        fontSize: '12px', 
                                        color: '#666',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        By: {img.submitted_by_name || 'Unknown'}
                                    </p>
                                    <p style={{ 
                                        margin: '4px 0 0 0', 
                                        fontSize: '11px', 
                                        color: '#999'
                                    }}>
                                        {img.category || 'No Category'}
                                    </p>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )
        }
    }

    return (
        <div className="pendingImages">
            <Typography variant="h6" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                Pending Images for Approval ({pendingImages.length})
            </Typography>
            {renderPendingImages()}
            
            {/* Preview/Approval Modal */}
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <div className="pendingImageOpen" style={{
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row-reverse', 
                    flex: '0.7',
                    gap: '30px', 
                    maxWidth: '800px',
                    margin: '50px auto',
                    maxHeight: '90vh'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
                        <div style={{
                            display: 'flex', 
                            backgroundColor: 'white', 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '30px',
                            cursor: 'pointer', 
                            alignItems: 'center', 
                            justifyContent: 'center'
                        }}
                        onClick={handleClose}>
                            <Crossicon />
                        </div>
                    </div>
                    <Card className="pendingApprovalModalContainer" style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        flex: 1, 
                        padding: '30px', 
                        maxHeight: '80vh', 
                        overflowY: 'auto',
                        gap: '30px'
                    }}>
                        {/* Image Preview */}
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{
                                backgroundColor: '#f5f5f5',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <img 
                                    src={getImageUrl(selectedItem?.url)} 
                                    alt={selectedItem?.title || ''} 
                                    style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '300px',
                                        objectFit: 'contain'
                                    }}
                                    onError={(e) => { 
                                        e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found'; 
                                    }}
                                />
                            </div>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleApprove}
                                disabled={actionLoading || rejectReason.length > 0}
                                fullWidth
                                sx={{ 
                                    mb: 1,
                                    backgroundColor: '#2e7d32',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#1b5e20',
                                        color: 'white',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#a5d6a7',
                                        color: '#666',
                                    }
                                }}
                            >
                                {actionLoading ? 'Processing...' : 'Approve'}
                            </Button>
                        </div>

                        {/* Image Details */}
                        <div style={{ flex: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>{selectedItem?.title || '-'}</Typography>
                            
                            <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>{selectedItem?.category || '-'}</Typography>
                            
                            <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>{selectedItem?.description || '-'}</Typography>
                            
                            <Typography variant="subtitle2" color="text.secondary">Tags</Typography>
                            <Box sx={{ mb: 2 }}>
                                {selectedItem?.tags && selectedItem.tags.length > 0 ? (
                                    selectedItem.tags.map((tag, idx) => (
                                        <Chip key={idx} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No tags</Typography>
                                )}
                            </Box>
                            
                            <Typography variant="subtitle2" color="text.secondary">Submitted By</Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedItem?.submitted_by_name || '-'}<br/>
                                <span style={{ fontSize: '12px', color: '#666' }}>{selectedItem?.submitted_by_email || ''}</span>
                            </Typography>
                            
                            <Typography variant="subtitle2" color="text.secondary">School Code</Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>{selectedItem?.school_code || '-'}</Typography>

                            {/* Reject Section */}
                            <div style={{ marginTop: '20px', borderTop: '1px solid #e0e0e0', paddingTop: '20px' }}>
                                <MSTextField 
                                    id="rejection" 
                                    type="text" 
                                    placeholder="Enter reason for rejection"
                                    label="Reason of Rejection (required to reject)" 
                                    fieldName="rejectReason" 
                                    value={rejectReason}
                                    onChange={handleFieldsChange}
                                />
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleReject}
                                    disabled={actionLoading || rejectReason.length === 0}
                                    fullWidth
                                    sx={{ 
                                        mt: 2,
                                        backgroundColor: '#d32f2f',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#b71c1c',
                                            color: 'white',
                                        },
                                        '&:disabled': {
                                            backgroundColor: '#ef9a9a',
                                            color: '#666',
                                        }
                                    }}
                                >
                                    {actionLoading ? 'Processing...' : 'Reject'}
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            </Modal>
        </div>
    )
}
export default PendingApproval;